import React, { ReactNode, useCallback } from 'react';

import styled, { css } from 'styled-components';

import theme from 'styles/theme';

import Link from 'components/Link';

import { ButtonTypes } from 'interfaces/content-stack/contentStack.types';

import { createEvent, pushAddAccountEvent, pushDataLayerEvent } from 'utils/dataLayerUtils';

type ButtonStyleProps = {
  $color?: string;
  $red?: boolean;
  $small?: boolean;
  $extraSmall?: boolean;
  $bold?: boolean;
  $faint?: boolean;
  $fullWidth?: boolean;
  $mobileHalfWidth?: boolean;
  $disableStretch?: boolean;
  backgroundImage?: string;
};

const LinkButton = styled(Link)<ButtonStyleProps>`
  grid-column-gap: 0.5rem;
  color: ${(props) => props.$color ?? props.theme.colors.white};
  align-items: center;
  font-weight: ${({ $bold, $faint }) => ($bold ? 600 : $faint ? 400 : 500)};
  font-family: ${(props) => props.theme.fonts.default};
  text-decoration: none;
  width: fit-content;
  border-bottom: 0.08rem solid rgba(0, 0, 0, 0);
  margin-bottom: -0.25rem;
  padding-bottom: 0.25rem;
  transition: border-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;

  &:hover {
    border-bottom-color: ${(props) => props.theme.colors.ttRed};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    align-self: center;
  }
`;

const RedFontAwesomeIcon = styled.div`
  color: ${({ theme }) => theme.colors.ttRed};
  font-family: ${({ theme }) => theme.fonts.faSolid900};
  font-size: 0.65rem;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  padding-left: 0.25rem;
  padding-top: 0.0625rem;
`;

const PillButton = styled(Link)<ButtonStyleProps>`
  border: 1px solid ${(props) => (props.$red ? props.theme.colors.ttRed : props.theme.colors.primaryBlack)};
  background-color: ${(props) => (props.$red ? props.theme.colors.ttRed : props.theme.colors.primaryBlack)};
  text-decoration: none;

  color: ${(props) => props.$color ?? props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.default};
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  border-radius: 5rem;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;
  ${(props) => props.$fullWidth && `width: 100%;`}
  background-image: ${(props) => (props.backgroundImage ? `url(${props.backgroundImage})` : 'none')};

  &:hover {
    ${(props) =>
      props.$red
        ? `
      background-color: ${props.theme.colors.darkerRed};
      border-color: ${props.theme.colors.darkerRed};
    `
        : `
      color: ${props.theme.colors.primaryBlack};
      background-color: ${props.$color ?? props.theme.colors.white};
      border-color: ${props.theme.colors.primaryBlack};
    `}
  }

  ${(props) =>
    props.backgroundImage &&
    css`
      width: 10rem;
      height: 2.5rem;
      border-radius: 0.5rem;
      border: none;
      background-position: center;
      background-size: cover;
      background-color: transparent;

      &:hover {
        background-color: transparent;
      }

      @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
        padding: 0.2rem 0;
      }
    `}

  ${({ $small }) =>
    $small &&
    css`
      padding: 0.45rem 1.5rem;
      font-size: 0.9rem;
    `}

  ${(props) =>
    props.$extraSmall &&
    `
    padding: 0.25rem 0.75rem;
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
  `}

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    align-self: ${(props) => (props.$mobileHalfWidth ? 'auto' : 'center')};
  }
`;

interface CtaButtonProps {
  className?: string;
  to?: string;
  buttonType?: ButtonTypes;
  text?: string | undefined;
  openInNewWindow?: boolean;
  textColor?: string;
  mobileHalfWidth?: boolean;
  small?: boolean;
  extraSmall?: boolean;
  bold?: boolean;
  faint?: boolean;
  children?: ReactNode;
  fullWidth?: boolean;
  disableStretch?: boolean;
  onClick?: () => void;
  backgroundImage?: string;
  banner?: boolean;
  dataLayerInfo?: string;
}

const CtaButton: React.FC<CtaButtonProps> = ({
  children,
  className,
  to,
  buttonType = 'primary',
  text,
  mobileHalfWidth,
  textColor,
  small,
  bold,
  faint,
  openInNewWindow,
  fullWidth,
  extraSmall,
  disableStretch,
  onClick,
  backgroundImage,
  banner,
  dataLayerInfo,
  ...props
}) => {
  const type = buttonType ?? 'primary';

  let color = textColor;
  if (!color) {
    if (type === 'link') {
      color = theme.colors.black;
    } else {
      color = theme.colors.white;
    }
  }

  const handleClick = useCallback(() => {
    pushAddAccountEvent(text ?? '', { interaction_type: 'click', interaction_value: text });

    const category = banner ? 'banner interaction' : 'cta interaction';
    const action = `click ${text}`;
    const label = dataLayerInfo ?? text ?? '';
    const additionalData = { interaction_type: action, interaction_value: dataLayerInfo };

    const eventData = createEvent('cta_interaction', category, action, label, additionalData);
    pushDataLayerEvent(eventData);

    if (onClick) {
      onClick();
    }
  }, [text, onClick, banner, dataLayerInfo]);

  if (type === 'default' || type === 'secondary') {
    return (
      <PillButton
        className={className}
        $color={color}
        to={to ?? ''}
        $small={small}
        $extraSmall={extraSmall}
        target={openInNewWindow ? '_blank' : '_self'}
        $fullWidth={fullWidth}
        $disableStretch={disableStretch}
        onClick={handleClick}
        backgroundImage={backgroundImage}
        {...props}
      >
        {text ?? 'Click Here'}
        {children}
      </PillButton>
    );
  } else if (type === 'primary') {
    return (
      <PillButton
        className={className}
        $color={color}
        $red={true}
        to={to ?? ''}
        $small={small}
        $extraSmall={extraSmall}
        $faint={faint}
        $mobileHalfWidth={mobileHalfWidth}
        target={openInNewWindow ? '_blank' : '_self'}
        $fullWidth={fullWidth}
        $disableStretch={disableStretch}
        onClick={handleClick}
        backgroundImage={backgroundImage}
        {...props}
      >
        {text ?? 'Click Here'}
        {children}
      </PillButton>
    );
  } else {
    return (
      <LinkButton
        className={className}
        to={to ?? ''}
        $color={color}
        $bold={bold}
        $faint={faint}
        target={openInNewWindow ? '_blank' : '_self'}
        onClick={handleClick}
        {...props}
      >
        {text ?? 'Click Here'}
        <RedFontAwesomeIcon>{'\uf178'}</RedFontAwesomeIcon>
      </LinkButton>
    );
  }
};

export default CtaButton;
